/**
 * BackgroundLoader - Intelligent background loading system with adaptive throttling
 * 
 * This system loads additional showrooms in the background while maintaining
 * smooth 60 FPS performance in the current room through adaptive throttling.
 * 
 * Key Features:
 * - Real-time performance monitoring
 * - Adaptive loading speed based on FPS
 * - Frame budget management (4-6ms per frame)
 * - User activity detection and smart pausing
 * - Memory pressure monitoring
 * - Progressive asset loading
 */
export class BackgroundLoader {
  constructor(multiRoomManager, roomManager, artworkManager) {
    this.multiRoomManager = multiRoomManager;
    this.roomManager = roomManager;
    this.artworkManager = artworkManager;
    
    // Performance monitoring
    this.targetFPS = 60;
    this.currentFPS = 60;
    this.frameCount = 0;
    this.lastFPSUpdate = performance.now();
    this.fpsHistory = [];
    
    // Loading control
    this.isLoading = false;
    this.isPaused = false;
    this.loadingQueue = [];
    this.currentLoadingItem = null;
    
    // Throttling parameters
    this.frameBudgetMs = 8; // Increased budget for more aggressive loading
    this.minDelayBetweenAssets = 50; // Reduced delay between assets (was 100ms)
    this.maxDelayBetweenAssets = 500; // Reduced max delay (was 2000ms)
    this.currentDelay = this.minDelayBetweenAssets;
    
    // User activity tracking
    this.lastUserActivity = performance.now();
    this.userActivityThreshold = 2000; // 2 seconds of inactivity before resuming
    this.isUserActive = false;
    
    // Performance thresholds - more lenient to allow loading during activity
    this.fpsThreshold = 45; // Only pause if FPS is really bad (was 50)
    this.memoryThreshold = 0.85; // Allow higher memory usage (was 0.8)
    
    // Mobile device detection and optimization
    this.isMobile = this.detectMobileDevice();
    this.isLowEndDevice = this.detectLowEndDevice();
    
    // Adjust parameters for mobile devices
    if (this.isMobile) {
      this.frameBudgetMs = 4; // Reduced budget for mobile
      this.minDelayBetweenAssets = 200; // Longer delays on mobile
      this.maxDelayBetweenAssets = 2000;
      this.fpsThreshold = 40; // Lower FPS threshold for mobile
      this.memoryThreshold = 0.75; // Stricter memory limits on mobile
    }
    
    if (this.isLowEndDevice) {
      this.frameBudgetMs = 2; // Very conservative for low-end devices
      this.minDelayBetweenAssets = 500;
      this.maxDelayBetweenAssets = 5000;
      this.fpsThreshold = 30;
      this.memoryThreshold = 0.6;
    }
    
    // Network error recovery settings
    this.maxRetries = 3;
    this.retryDelayBase = 1000; // Base delay: 1s, 2s, 4s for exponential backoff
    this.failedItems = new Map(); // Track failed items and retry counts
    this.networkTimeoutMs = 30000; // 30s timeout for individual requests
    
    // Start performance monitoring
    this.startPerformanceMonitoring();
    this.startUserActivityMonitoring();
    
    console.log('🚀 BackgroundLoader initialized with adaptive throttling');
  }

  /**
   * Start monitoring FPS and performance metrics
   */
  startPerformanceMonitoring() {
    const updateFPS = () => {
      const now = performance.now();
      this.frameCount++;
      
      // Update FPS every second
      if (now - this.lastFPSUpdate >= 1000) {
        this.currentFPS = (this.frameCount * 1000) / (now - this.lastFPSUpdate);
        this.fpsHistory.push(this.currentFPS);
        
        // Keep only last 10 FPS readings
        if (this.fpsHistory.length > 10) {
          this.fpsHistory.shift();
        }
        
        // Reset counters
        this.frameCount = 0;
        this.lastFPSUpdate = now;
        
        // Adjust loading speed based on performance
        this.adaptLoadingSpeed();
      }
      
      requestAnimationFrame(updateFPS);
    };
    
    requestAnimationFrame(updateFPS);
  }

  /**
   * Track user activity to pause loading during interactions
   */
  startUserActivityMonitoring() {
    const events = ['mousemove', 'mousedown', 'touchstart', 'touchmove', 'keydown'];
    
    const onUserActivity = () => {
      this.lastUserActivity = performance.now();
      this.isUserActive = true;
    };
    
    events.forEach(event => {
      document.addEventListener(event, onUserActivity, { passive: true });
    });
    
    // Check activity status periodically
    setInterval(() => {
      const timeSinceActivity = performance.now() - this.lastUserActivity;
      this.isUserActive = timeSinceActivity < this.userActivityThreshold;
    }, 500);
  }

  /**
   * Adapt loading speed based on current performance
   */
  adaptLoadingSpeed() {
    const avgFPS = this.fpsHistory.length > 0 ? 
      this.fpsHistory.reduce((a, b) => a + b) / this.fpsHistory.length : 60;
    
    // Calculate performance ratio (1.0 = perfect 60fps, 0.5 = 30fps)
    const performanceRatio = Math.min(avgFPS / this.targetFPS, 1.0);
    
    // Adjust delay based on performance
    if (performanceRatio > 0.95) {
      // Excellent performance - load faster
      this.currentDelay = this.minDelayBetweenAssets;
      this.frameBudgetMs = 6; // Allow more time per frame
    } else if (performanceRatio > 0.85) {
      // Good performance - normal speed
      this.currentDelay = this.minDelayBetweenAssets * 1.5;
      this.frameBudgetMs = 5;
    } else if (performanceRatio > 0.70) {
      // Moderate performance - slow down
      this.currentDelay = this.minDelayBetweenAssets * 3;
      this.frameBudgetMs = 4;
    } else {
      // Poor performance - very slow loading
      this.currentDelay = this.maxDelayBetweenAssets;
      this.frameBudgetMs = 3;
    }
    
    // Performance adapted silently
  }

  /**
   * Check if loading should be paused due to performance or activity
   */
  shouldPauseLoading() {
    // Only pause for performance reasons, NOT user activity
    
    // Pause if FPS is too low
    if (this.currentFPS < this.fpsThreshold) {
      return true;
    }
    
    // Pause if memory pressure is high
    if (performance.memory) {
      const memoryUsage = performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit;
      if (memoryUsage > this.memoryThreshold) {
        console.warn(`⚠️ High memory usage: ${(memoryUsage * 100).toFixed(1)}%`);
        return true;
      }
    }
    
    return false;
  }

  /**
   * Add rooms to the background loading queue
   */
  queueRoomsForLoading(showrooms, items, placements, currentRoomId) {
    console.log(`📋 Queuing ${showrooms.length} rooms for background loading`);
    
    // Clear existing queue
    this.loadingQueue = [];
    
    // Add rooms to queue (excluding current room)
    showrooms.forEach(showroom => {
      if (showroom.id !== currentRoomId) {
        const roomPlacements = placements.filter(p => p.showroomId === showroom.id);
        const roomItems = roomPlacements.map(p => items.find(i => i.id === p.itemId)).filter(Boolean);
        
        this.loadingQueue.push({
          type: 'room',
          showroom,
          placements: roomPlacements,
          items: roomItems,
          priority: 1 // All background rooms have same priority for now
        });
      }
    });
    
    console.log(`✅ Queued ${this.loadingQueue.length} rooms for background loading`);
  }

  /**
   * Start background loading process
   */
  async startBackgroundLoading() {
    if (this.isLoading) {
      console.log('⏩ Background loading already in progress');
      return;
    }
    
    console.log('🚀 Starting intelligent background loading');
    this.isLoading = true;
    
    try {
      await this.processLoadingQueue();
    } catch (error) {
      console.error('❌ Background loading error:', error);
    } finally {
      this.isLoading = false;
      console.log('✅ Background loading completed');
    }
  }

  /**
   * Process the loading queue with intelligent throttling
   */
  async processLoadingQueue() {
    while (this.loadingQueue.length > 0 && this.isLoading) {
      // Check if we should pause loading
      if (this.shouldPauseLoading()) {
        await this.waitForBetterConditions();
        continue;
      }
      
      // Get next item to load
      const nextItem = this.loadingQueue.shift();
      this.currentLoadingItem = nextItem;
      
      // Apply mobile-specific optimizations
      await this.applyMobileOptimizations(nextItem);
      
      try {
        await this.loadRoomInBackground(nextItem);
      } catch (error) {
        const shouldRetry = this.handleLoadingError(nextItem, error);
        if (!shouldRetry) {
          console.error(`❌ Failed to load room ${nextItem.showroom.id} (max retries exceeded):`, error);
          this.multiRoomManager.markRoomAsError(nextItem.showroom.id, error);
        }
      }
      
      // Wait before next load (adaptive delay)
      if (this.loadingQueue.length > 0) {
        await this.delay(this.currentDelay);
      }
    }
    
    this.currentLoadingItem = null;
  }

  /**
   * Load a single room in background with frame budget management
   */
  async loadRoomInBackground(roomData) {
    const { showroom, placements, items } = roomData;
    const roomId = showroom.id;
    
    // Mark room as loading
    this.multiRoomManager.markRoomAsLoading(roomId);
    
    // Create container for this room
    const container = this.multiRoomManager.createRoomContainer(roomId);
    
    try {
      // Load room geometry with frame budget management
      await this.loadWithFrameBudget(async () => {
        await this.roomManager.createRoom(showroom, container);
      }, `Room geometry for ${roomId}`);
      
      // Load artworks one by one with frame budget
      for (let i = 0; i < placements.length; i++) {
        const placement = placements[i];
        const item = items.find(itm => itm.id === placement.itemId);
        
        if (item) {
          await this.loadWithFrameBudget(async () => {
            await this.artworkManager.addArtwork(item, placement, this.roomManager.getPlacementSystem(), container);
          }, `Artwork ${item.name} in room ${roomId}`);
          
          // Small delay between artworks
          await this.delay(Math.min(this.currentDelay / 3, 200));
        }
        
        // Check if we should pause between artworks
        if (this.shouldPauseLoading()) {
          await this.waitForBetterConditions();
        }
      }
      
      // Mark room as fully loaded
      this.multiRoomManager.markRoomAsLoaded(roomId);
      console.log(`✅ Room ${roomId} ready for instant switching`);
      
    } catch (error) {
      console.error(`❌ Error loading room ${roomId}:`, error);
      this.multiRoomManager.markRoomAsError(roomId, error);
      throw error;
    }
  }

  /**
   * Execute a loading operation within frame budget
   */
  async loadWithFrameBudget(loadFunction, description) {
    const startTime = performance.now();
    
    try {
      await loadFunction();
      
      const loadTime = performance.now() - startTime;
      console.log(`⏱️ ${description} loaded in ${loadTime.toFixed(1)}ms`);
      
      // If we exceeded frame budget, increase delay for next operation
      if (loadTime > this.frameBudgetMs * 2) {
        this.currentDelay = Math.min(this.currentDelay * 1.2, this.maxDelayBetweenAssets);
        console.log(`🐌 Increased delay to ${this.currentDelay}ms due to slow loading`);
      }
      
    } catch (error) {
      console.error(`❌ Failed to load ${description}:`, error);
      throw error;
    }
  }

  /**
   * Handle loading errors with retry logic
   * @param {Object} loadingItem - The item that failed to load
   * @param {Error} error - The error that occurred
   * @returns {boolean} - Whether to retry the item
   */
  handleLoadingError(loadingItem, error) {
    const itemKey = `${loadingItem.showroom.id}`;
    const failureInfo = this.failedItems.get(itemKey) || { count: 0, lastAttempt: 0 };
    
    // Classify error type
    const errorType = this.classifyNetworkError(error);
    
    // Don't retry certain types of errors
    if (errorType === 'non-retryable') {
      console.error(`💀 Non-retryable error for room ${itemKey}:`, error.message);
      return false;
    }
    
    failureInfo.count++;
    failureInfo.lastAttempt = Date.now();
    this.failedItems.set(itemKey, failureInfo);
    
    if (failureInfo.count >= this.maxRetries) {
      console.error(`💀 Max retries (${this.maxRetries}) exceeded for room ${itemKey}`);
      return false;
    }
    
    // Calculate retry delay with exponential backoff
    const retryDelay = this.retryDelayBase * Math.pow(2, failureInfo.count - 1);
    
    console.log(`🔄 Retry ${failureInfo.count}/${this.maxRetries} for room ${itemKey} in ${retryDelay}ms (${errorType})`);
    
    // Schedule retry
    setTimeout(() => {
      // Add back to queue with priority
      this.loadingQueue.unshift(loadingItem);
    }, retryDelay);
    
    return true;
  }

  /**
   * Classify network errors to determine retry strategy
   * @param {Error} error - The error to classify
   * @returns {string} - Error classification
   */
  classifyNetworkError(error) {
    const message = error.message.toLowerCase();
    
    // Network connectivity issues (retryable)
    if (message.includes('network') || 
        message.includes('fetch') || 
        message.includes('timeout') ||
        message.includes('connection') ||
        error.name === 'NetworkError' ||
        error.name === 'TypeError') {
      return 'network';
    }
    
    // Memory issues (retryable with cleanup)
    if (message.includes('memory') || 
        message.includes('allocation') ||
        message.includes('heap')) {
      return 'memory';
    }
    
    // File format or corruption issues (non-retryable)
    if (message.includes('parse') || 
        message.includes('format') ||
        message.includes('syntax') ||
        message.includes('invalid')) {
      return 'non-retryable';
    }
    
    // Server errors (retryable)
    if (message.includes('404') || 
        message.includes('500') ||
        message.includes('502') ||
        message.includes('503')) {
      return 'server';
    }
    
    // Default to retryable
    return 'unknown';
  }

  /**
   * Detect mobile devices
   * @returns {boolean} True if mobile device
   */
  detectMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Detect low-end devices that need aggressive optimization
   * @returns {boolean} True if low-end device
   */
  detectLowEndDevice() {
    // Device memory detection
    if (navigator.deviceMemory && navigator.deviceMemory <= 2) {
      return true;
    }
    
    // Hardware concurrency (CPU cores)
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) {
      return true;
    }
    
    // Connection speed detection
    if (navigator.connection) {
      const slowConnections = ['slow-2g', '2g', '3g'];
      if (slowConnections.includes(navigator.connection.effectiveType)) {
        return true;
      }
    }
    
    // Older mobile devices
    const lowEndPatterns = [
      /Android [0-4]\./i,
      /iPhone OS [0-9]\./i,
      /CPU OS [0-9]_/i
    ];
    
    return lowEndPatterns.some(pattern => pattern.test(navigator.userAgent));
  }

  /**
   * Apply mobile-specific optimizations during loading
   * @param {Object} loadingItem - Item being loaded
   */
  async applyMobileOptimizations(loadingItem) {
    if (!this.isMobile && !this.isLowEndDevice) {
      return; // No mobile optimizations needed
    }

    // Check battery status on mobile
    if ('getBattery' in navigator) {
      try {
        const battery = await navigator.getBattery();
        
        // Pause loading if battery is low and not charging
        if (battery.level < 0.2 && !battery.charging) {
          console.log('🔋 Low battery detected - pausing background loading');
          this.isPaused = true;
          
          // Resume when battery improves or starts charging
          const resumeHandler = () => {
            if (battery.level > 0.25 || battery.charging) {
              console.log('🔋 Battery improved - resuming background loading');
              this.isPaused = false;
              battery.removeEventListener('levelchange', resumeHandler);
              battery.removeEventListener('chargingchange', resumeHandler);
            }
          };
          
          battery.addEventListener('levelchange', resumeHandler);
          battery.addEventListener('chargingchange', resumeHandler);
          return;
        }
      } catch (error) {
        // Battery API not available or failed
      }
    }

    // Reduce loading frequency on mobile during poor conditions
    if (this.isLowEndDevice) {
      // Check if device is under thermal stress (approximated)
      const currentTime = Date.now();
      if (this.currentFPS < 20) {
        console.log('📱 Low-end device under stress - extending delays');
        this.currentDelay = Math.min(this.currentDelay * 2, this.maxDelayBetweenAssets);
      }
    }
  }

  /**
   * Wait for better conditions to resume loading
   */
  async waitForBetterConditions() {
    const maxWaitTime = 10000; // Max 10 seconds
    const startWait = performance.now();
    
    while (this.shouldPauseLoading() && (performance.now() - startWait) < maxWaitTime) {
      await this.delay(500); // Check every 500ms
    }
    
    // Timeout or conditions improved
  }

  /**
   * Utility delay function
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Stop background loading
   */
  stopLoading() {
    console.log('🛑 Stopping background loading');
    this.isLoading = false;
    this.currentLoadingItem = null;
    this.loadingQueue = [];
  }

  /**
   * Get current loading progress
   */
  getProgress() {
    const totalRooms = this.loadingQueue.length + (this.currentLoadingItem ? 1 : 0);
    const completedRooms = this.multiRoomManager.getLoadingProgress().loaded;
    
    return {
      totalRooms,
      completedRooms,
      currentlyLoading: this.currentLoadingItem?.showroom?.name || null,
      isActive: this.isLoading,
      isPaused: this.isPaused || this.shouldPauseLoading(),
      currentFPS: this.currentFPS,
      currentDelay: this.currentDelay,
      queueRemaining: this.loadingQueue.length
    };
  }

  /**
   * Force pause/resume loading
   */
  pause() {
    this.isPaused = true;
    console.log('⏸️ Background loading manually paused');
  }

  resume() {
    this.isPaused = false;
    console.log('▶️ Background loading manually resumed');
  }
}